import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  highSchoolSearchService, 
  HighSchool, 
  HighSchoolSuggestion,
  HighSchoolSearchRequest 
} from '@/services/highSchoolSearchService';

export const useHighSchoolSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search high schools
  const {
    data: searchResults,
    isLoading: isSearching,
    error: searchError,
    refetch: refetchSearch
  } = useQuery({
    queryKey: ['highSchoolSearch', debouncedQuery, selectedState, selectedCity],
    queryFn: async () => {
      if (!debouncedQuery.trim()) {
        return { schools: [], total_count: 0, query: '' };
      }

      const request: HighSchoolSearchRequest = {
        query: debouncedQuery,
        state: selectedState || undefined,
        city: selectedCity || undefined,
        limit: 20
      };

      return highSchoolSearchService.searchHighSchools(request);
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get available states
  const {
    data: availableStates,
    isLoading: isLoadingStates
  } = useQuery({
    queryKey: ['highSchoolStates'],
    queryFn: () => highSchoolSearchService.getAvailableStates(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Get popular schools
  const {
    data: popularSchools,
    isLoading: isLoadingPopular
  } = useQuery({
    queryKey: ['popularHighSchools', selectedState],
    queryFn: () => highSchoolSearchService.getPopularHighSchools(selectedState || undefined, 10),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSelectedState('');
    setSelectedCity('');
  }, []);

  const searchByLocation = useCallback(async (state: string, city?: string) => {
    try {
      return await highSchoolSearchService.getHighSchoolsByLocation(state, city);
    } catch (error) {
      console.error('Error searching by location:', error);
      return [];
    }
  }, []);

  return {
    // Search state
    searchQuery,
    setSearchQuery,
    selectedState,
    setSelectedState,
    selectedCity,
    setSelectedCity,
    
    // Search results
    searchResults: searchResults?.schools || [],
    totalCount: searchResults?.total_count || 0,
    isSearching,
    searchError,
    
    // Available data
    availableStates: availableStates || [],
    isLoadingStates,
    popularSchools: popularSchools || [],
    isLoadingPopular,
    
    // Actions
    clearSearch,
    refetchSearch,
    searchByLocation,
  };
};

export const useHighSchoolSuggestions = (query: string, limit: number = 5) => {
  const [suggestions, setSuggestions] = useState<HighSchoolSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getSuggestions = async () => {
      if (!query || query.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await highSchoolSearchService.suggestHighSchools(query, limit);
        setSuggestions(results);
      } catch (error) {
        console.error('Error getting suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(getSuggestions, 300);
    return () => clearTimeout(timer);
  }, [query, limit]);

  return {
    suggestions,
    isLoading,
  };
};

export const useHighSchoolDetails = (schoolId: number | null) => {
  return useQuery({
    queryKey: ['highSchoolDetails', schoolId],
    queryFn: () => schoolId ? highSchoolSearchService.getHighSchoolById(schoolId) : null,
    enabled: !!schoolId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};